import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Phone, Search, MapPin, User, Clock, Car, Truck, Calendar, Navigation, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../config/api';
import DirectionsButton from './DirectionsButton';

const CheckBooking = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!phoneNumber) {
      toast.error('Please enter your phone number');
      return;
    }

    setLoading(true);
    try {
      console.log('Fetching bookings for phone number:', phoneNumber);
      const response = await api.get(`/public/vehicle-bookings/check/${phoneNumber}`);
      console.log('API response:', response.data);
      
      setBookings(response.data.data || []);
      if (response.data.data?.length === 0) {
        toast.error('No bookings found for this phone number');
      } else {
        toast.success(`Found ${response.data.data?.length} booking(s)`);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'cancelled':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12 px-4 sm:px-6">
      <div className="max-w-6xl w-full mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl mb-6 shadow-lg">
            <Truck className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Track Your Emergency Vehicle
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Enter your phone number to check the status of your vehicle booking and get real-time updates
          </p>
        </motion.div>

        {/* Search Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl border border-gray-200 p-8 mb-12"
        >
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Check Booking Status
              </h2>
              <p className="text-gray-600">
                Enter your registered phone number to track your vehicle booking
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Phone className="h-6 w-6 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phone"
                  className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                  placeholder="Enter 10-digit phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  maxLength="10"
                  pattern="[0-9]{10}"
                  title="Please enter a valid 10-digit phone number"
                  required
                />
              </div>
              <motion.button
                type="submit"
                className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-xl flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Search Bookings
                  </>
                )}
              </motion.button>
            </div>
          </form>
        </motion.div>
        
        {/* Results Section */}
        <AnimatePresence>
          {bookings.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Your Bookings
                </h3>
                <p className="text-gray-600">
                  Found {bookings.length} booking{bookings.length > 1 ? 's' : ''}
                </p>
              </div>

              {bookings.map((booking, index) => (
                <motion.div
                  key={booking._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
                >
                  {/* Booking Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
                          <Truck className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900">
                            Booking #{booking._id.slice(-6)}
                          </h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(booking.status)}`}>
                              {getStatusIcon(booking.status)}
                              <span className="ml-1">{booking.status}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center text-gray-600 mb-1">
                          <Calendar className="w-4 h-4 mr-2" />
                          <span className="font-medium">{booking.date}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Clock className="w-4 h-4 mr-2" />
                          <span className="font-medium">{booking.time}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Booking Details */}
                  <div className="p-8">
                    {/* Location Details */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                      <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200">
                        <div className="flex items-center mb-4">
                          <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center mr-3">
                            <MapPin className="w-5 h-5 text-white" />
                          </div>
                          <h5 className="text-lg font-bold text-gray-900">Pickup Location</h5>
                        </div>
                        <p className="text-gray-800 font-medium mb-2">{booking.pickupLocation}</p>
                        <p className="text-gray-600 text-sm mb-4">
                          {booking.pickupCity}, {booking.pickupState}
                        </p>
                        <DirectionsButton coordinates={booking.pickupCoordinates} />
                      </div>

                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                        <div className="flex items-center mb-4">
                          <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center mr-3">
                            <Navigation className="w-5 h-5 text-white" />
                          </div>
                          <h5 className="text-lg font-bold text-gray-900">Drop Location</h5>
                        </div>
                        <p className="text-gray-800 font-medium mb-2">{booking.dropLocation}</p>
                        <p className="text-gray-600 text-sm">
                          {booking.dropCity}, {booking.dropState}
                        </p>
                      </div>
                    </div>

                    {/* Trip Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div className="bg-gray-50 rounded-2xl p-6">
                        <h5 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <Clock className="w-5 h-5 mr-2 text-blue-500" />
                          Trip Details
                        </h5>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Time:</span>
                            <span className="font-medium text-gray-900">{booking.time}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Passengers:</span>
                            <span className="font-medium text-gray-900">{booking.passengers || 1}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-2xl p-6">
                        <h5 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <User className="w-5 h-5 mr-2 text-green-500" />
                          Purpose of Trip
                        </h5>
                        <p className="text-gray-800 font-medium">
                          {booking.purpose || 'Not specified'}
                        </p>
                      </div>
                    </div>

                    {/* Driver Information */}
                    {booking.status === 'approved' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200"
                      >
                        <h5 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                          Driver Information
                        </h5>
                        {booking.driverInfo ? (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                                <User className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Driver</p>
                                <p className="font-semibold text-gray-900">{booking.driverInfo.name}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                                <Phone className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Contact</p>
                                <p className="font-semibold text-gray-900">{booking.driverInfo.contactNumber}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                                <Car className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Vehicle No</p>
                                <p className="font-semibold text-gray-900">{booking.driverInfo.vehicleNumber}</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-3 text-yellow-700">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Driver information is being loaded...</span>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CheckBooking;
